import { Star } from '@mui/icons-material'
import { Grid, Paper, Tooltip } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo } from 'react'
import { getI18n, useTranslation } from 'react-i18next'
import history from '../../../history'
import { ISkillFormPackage } from '../../../interfaces/skills/ISkills'
import { useStore } from '../../../Provider'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import { Data, OPNewTable } from '../../OPcomponents/OPNewTable'
import { OPSkillbar } from '../../OPcomponents/OPSkillbar'
import OpenSkill from '../../Skills/OpenSkill'

const SkillTable = (): JSX.Element => {
  const { t } = useTranslation()
  const rootStore = useStore()

  const { validatedUser } = rootStore.loginStore
  const { filteredSkills, loadingSkills, getAllUserSkills } = rootStore.personStore
  const { setCurrentskill, allSkills, getUserDetailedSkillLinkedProjects, showEditSkillModalHandler, showNewSkillModal, getAllSkills } =
    rootStore.skillStore
  const { getSkillPersons } = rootStore.seachStore
  const loadingAllSkills = rootStore.skillStore.loadingSkills

  const profileClickedId = history.location.state as string

  const openSkillRow = useCallback(
    async (id: number | string) => {
      const curClicked = allSkills.find((skill) => skill.skill.skillId === Number(id))

      // Guard: Check if skill exists before making async calls
      if (!curClicked?.skill) {
        console.warn(`SkillTable.openSkillRow: Skill with ID ${id} not found in allSkills array`)
        return undefined
      }

      const userSkill = await getUserDetailedSkillLinkedProjects(validatedUser, Number(id))
      const skillPersons = await getSkillPersons(Number(id))

      const skillInfo: ISkillFormPackage = {
        skill: { skill: curClicked.skill, userInterested: curClicked.userInterested },
        userSkill: userSkill.skill,
        isNew: userSkill.isNew,
        skillPersons: skillPersons,
      }
      return skillInfo
    },
    [allSkills, getSkillPersons, getUserDetailedSkillLinkedProjects, validatedUser],
  )

  const onRowClose = useCallback(() => {
    Promise.allSettled([getAllSkills(validatedUser, false), getAllUserSkills(validatedUser, false)])
  }, [getAllSkills, getAllUserSkills, validatedUser])

  useEffect(() => {
    return () => {
      showEditSkillModalHandler(false)
      setCurrentskill({})
    }
    // cleanup on unmount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rows: Data[] = useMemo(() => {
    const lang = getI18n().language
    // Build a map for O(1) metadata lookups
    const skillMetaMap = new Map(allSkills.map((s) => [s.skill.skillId, s.skill]))

    return filteredSkills.map((skill) => {
      const meta = skillMetaMap.get(skill.skillId)
      const parentId = meta?.parentSkillId ?? null
      const parent = parentId ? skillMetaMap.get(parentId) : undefined
      const parentName = parent ? (lang === 'en' && parent.skillNameEng?.length ? parent.skillNameEng : parent.skillNameFi) : ''

      // Projects: show count, names on hover (unique names)
      const projectNames = Array.from(new Set<string>(skill.linkedProjectNames ?? []))
      const projectsCount = projectNames.length
      const projectsTitle = projectNames.join(', ')

      const displayName = lang === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi

      return {
        id: skill.skillId,
        rows: {
          [t('Skills.SkillName')]: {
            text: displayName,
            width: '23%',
          },
          [t('Skills.ParentSkill')]: {
            text: parentName || t('Projects.NotDefined'),
            width: '14%',
          },
          [t('Skills.Category')]: {
            text: meta?.categoryName ?? t('Projects.NotDefined'),
            width: '19%',
          },
          [t('Skills.Level')]: {
            text: <OPSkillbar name={displayName} value={skill.skillLevel} readonly />,
            width: '20%',
          },
          [t('Skills.KeySkill')]: {
            text: skill.isKeySkill ? <Star color='primary' /> : '',
            width: '8%',
            align: 'center' as const,
          },
          [t('Skills.Expirience')]: { text: `${skill.experienceDays} ${t('Date.ExperienceDays')} `, width: '8%', align: 'right' as const },
          [t('Skills.Projects')]: {
            text: (
              <Tooltip title={projectsTitle} placement='bottom' arrow>
                <span>{projectsCount}</span>
              </Tooltip>
            ),
            width: '8%',
            align: 'right' as const,
          },
        },
      } as Data
    })
  }, [allSkills, filteredSkills, t])

  return (
    <>
      {loadingSkills || loadingAllSkills ? (
        <Paper className='profile--paper__loadingskills'>
          <OPLoadingCircle />
        </Paper>
      ) : rows.length === 0 ? (
        <>
          {!showNewSkillModal && (
            <Paper className='profile--paper__addtitlepadding'>
              <Grid container textAlign='center'>
                <Grid item xs={12}>
                  <p className='profile--deptext'>{t('Skills.SkillsPlaceholder')}</p>
                </Grid>
              </Grid>
            </Paper>
          )}
        </>
      ) : (
        <OPNewTable
          profileclickedId={profileClickedId}
          rows={rows}
          curLength={rows.length}
          onRowClose={onRowClose}
          FormBase={OpenSkill}
          onRowClick={openSkillRow}
        />
      )}
    </>
  )
}

export default observer(SkillTable)
